import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import FormHeader from "@/components/ui/organisms/MHeader";
import { useProfileField } from "@/hooks/useHealthProfile";
import { IDeviceImplant, MedicalDetailType } from "@/types/health-profile";
import { getMemberInfo } from "@/hooks/useMembers";
import { useToast } from "@/components/ui/atoms/MToastProvider";
import { ListItem } from "@/components/ui/atoms/MListItem";
// Removed unused FooterDrawerDoneButton import

import { IconEdit, IconCircleX } from "@tabler/icons-react";
import MLoader from "@/components/ui/atoms/MLoader";
import MDialog from "@/components/ui/atoms/MDialog";
import { useTranslation } from "react-i18next";
import { formattedYear } from "@/utils/date-utils";
import { useNavigationSource } from '@/hooks/useNavigationSource';

const ImplantedDevices: React.FC = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  
  // Navigation source
  const { navigateBackIfExists } = useNavigationSource();
  // Removed unused showAddDrawer and editingDevice state
  const memberInfo = getMemberInfo();
  const memberId = memberInfo.id;
  const {
    fieldValue: deviceImplants = [],
    saveField: saveDeviceImplants,
    loading,
  } = useProfileField<IDeviceImplant[]>(
    memberId,
    MedicalDetailType.DEVICE_IMPLANTS,
    []
  );

  const navigate = useNavigate();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deviceToDelete, setDeviceToDelete] = useState<IDeviceImplant | null>(
    null
  );

  const handleDelete = async () => {
    if (!deviceToDelete) return;
    try {
      const newDevices = deviceImplants.filter((d) => d !== deviceToDelete);
      await saveDeviceImplants(newDevices);
      if (newDevices.length === 0) {
        // Navigate back to source if exists, otherwise go to health profile
        const didNavigate = navigateBackIfExists();
        if (!didNavigate) {
          navigate("/profile/health-profile");
        }
      }
    } catch (error) {
      showToast({
        message: t("errors.toast"),
        variant: "error",
      });
    } finally {
      setShowDeleteModal(false);
      setDeviceToDelete(null);
    }

  };

  const getSecondaryText = (device: IDeviceImplant) => {
    if (device.date) {
      return t("implanted_devices.implantedIn", { year: formattedYear(device.date) });
    }
    return t("implanted_devices.yearNotAnswered");
  };

  if (loading) return <MLoader />;

  return (
    <div className="profile-page-root">
      <FormHeader
        module="implanted_devices"
        memberInfo={memberInfo}
        onAdd={() =>
          navigate("/profile/health-profile/implant-devices/form?mode=add")
        }
        showGradient
        onBack={() => {
          const didNavigate = navigateBackIfExists();
          if (!didNavigate) {
            navigate("/profile/health-profile");
          }
        }}
      />
      <div className="profile-flex-col-gap">
        {deviceImplants.map((device, idx) => (
          <ListItem
            usage="crud"
            type="2-line"
            state="default"
            variant="purple"
            title={device.name}
            secondaryText={getSecondaryText(device)}
            actions={[
              {
                id: "edit",
                icon: <IconEdit />,
                label: t("common.edit"),
                onClick: () => {
                  navigate(
                    `/profile/health-profile/implant-devices/form?mode=edit&index=${idx}`
                  );
                },
              },
              {
                id: "delete",
                label: t("common.delete"),
                icon: <IconCircleX />,
                onClick: () => {
                  setDeviceToDelete(device);
                  setShowDeleteModal(true);
                },
                className: "delete",
              },
            ]}
          />
        ))}
      </div>
      <MDialog
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setDeviceToDelete(null);
        }}
        title={t("implanted_devices.deleteThisRecord")}
        description={t(`implanted_devices.deleteThisRecordDescription${memberInfo.onBehalf ? "_on_behalf" : ""}`)}
        secondaryCtaText={t("common.no")}
        onSecondaryCta={() => {
          setShowDeleteModal(false);
          setDeviceToDelete(null);
        }}
        primaryCtaText={t("common.yes")}
        onPrimaryCta={handleDelete}
      />
    </div>
  );
};

export default ImplantedDevices;
